import {
  selectedNoteTextAtom,
  type SelectedNoteText,
} from '@/atoms/selectedNoteTextAtoms';
import { useIsSelectionAskHeidiEnabled } from '@/features/flags';
import { useAtom } from 'jotai';
import { useCallback } from 'react';

export interface UseSelectedNoteTextContextOptions {
  /**
   * When provided, selection context is only active while a session is
   * present (chat panel). Omit for surfaces that attach selection without
   * a session gate (floating Ask Heidi bar).
   */
  sessionId?: string | null;
}

export interface UseSelectedNoteTextContextResult {
  hasSelectedTextContext: boolean;
  selectedNoteText: SelectedNoteText | null;
  clearSelectedNoteText: () => void;
  setSelectedNoteText: (value: SelectedNoteText | null) => void;
}

/**
 * Selection → Ask Heidi: highlighted note text attached as context.
 *
 * Lives outside `src/components/chat/**` so Chat callers can use the flag
 * kill switch without adding a shrink-only chat→features/flags outbound edge.
 *
 * `useSlashShortcutMenu` is re-exported here because ChatInput already imports
 * this module. A second `@/components/ui` specifier would grow K2; putting the
 * hook on `protocols-button` trips `react-refresh/only-export-components`.
 */
export { useSlashShortcutMenu } from './use-slash-shortcut-menu';

/**
 * Selection → Ask Heidi: highlighted note text attached as context.
 *
 * Lives outside `src/components/chat/**` so Chat callers can use the flag
 * kill switch without adding a shrink-only chat→features/flags outbound edge.
 */
export function useSelectedNoteTextContext(
  options: UseSelectedNoteTextContextOptions = {}
): UseSelectedNoteTextContextResult {
  const { sessionId } = options;
  const isSelectionAskHeidiEnabled = useIsSelectionAskHeidiEnabled();
  const [selectedNoteText, setSelectedNoteText] = useAtom(selectedNoteTextAtom);

  const requiresSession = sessionId !== undefined;
  const hasSession = !!sessionId;
  const hasSelectedTextContext =
    isSelectionAskHeidiEnabled && !!selectedNoteText && (!requiresSession || hasSession);

  const clearSelectedNoteText = useCallback(
    () => setSelectedNoteText(null),
    [setSelectedNoteText]
  );

  return {
    hasSelectedTextContext,
    selectedNoteText,
    clearSelectedNoteText,
    setSelectedNoteText,
  };
}
